import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
	emailSchedulerControllerCancelMutation,
	emailSchedulerControllerRescheduleMutation,
	emailSchedulerControllerRestartMutation,
	emailSchedulerControllerSendNowMutation,
	emailSchedulerControllerListSchedulesQueryKey,
} from '@dealstack/shared/lib/backendClient/@tanstack/react-query.gen';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import type { UseScheduledEmailActionsArgs } from './useScheduledEmailActions.types';

export const useScheduledEmailActions = ({
	dealId,
	programmeId,
	onSettled,
}: UseScheduledEmailActionsArgs) => {
	const tMipManage = useTranslationMipManage();
	const queryClient = useQueryClient();

	const invalidateSchedules = () =>
		queryClient.invalidateQueries({
			queryKey: emailSchedulerControllerListSchedulesQueryKey({
				path: { dealId, programmeId },
			}),
		});

	const cancelMutation = useMutation({
		...emailSchedulerControllerCancelMutation(),
		onSuccess: async () => {
			showToastV2({
				title: tMipManage('email_scheduler.toast.cancel.successful'),
				state: 'success',
			});
			await invalidateSchedules();
		},
		onError: () => {
			showToastV2({
				state: 'error',
				title: tMipManage('email_scheduler.toast.cancel.error'),
			});
		},
		onSettled,
	});

	const rescheduleMutation = useMutation({
		...emailSchedulerControllerRescheduleMutation(),
		onSuccess: async () => {
			showToastV2({
				title: tMipManage('email_scheduler.toast.reschedule.successful'),
				state: 'success',
			});
			await invalidateSchedules();
		},
		onError: () => {
			showToastV2({
				state: 'error',
				title: tMipManage('email_scheduler.toast.reschedule.error'),
			});
		},
		onSettled,
	});

	const restartMutation = useMutation({
		...emailSchedulerControllerRestartMutation(),
		onSuccess: async () => {
			showToastV2({
				title: tMipManage('email_scheduler.toast.restart.successful'),
				state: 'success',
			});
			await invalidateSchedules();
		},
		onError: () => {
			showToastV2({
				state: 'error',
				title: tMipManage('email_scheduler.toast.restart.error'),
			});
		},
		onSettled,
	});

	const sendNowMutation = useMutation({
		...emailSchedulerControllerSendNowMutation(),
		onSuccess: async () => {
			showToastV2({
				title: tMipManage('email_scheduler.toast.send_now.successful'),
				state: 'success',
			});
			await invalidateSchedules();
		},
		onError: () => {
			showToastV2({
				state: 'error',
				title: tMipManage('email_scheduler.toast.send_now.error'),
			});
		},
		onSettled,
	});

	return {
		cancel: (id: string) => cancelMutation.mutate({ path: { dealId, programmeId, id } }),
		isCancelPending: cancelMutation.isPending,
		reschedule: (id: string, nextFireAt: string) =>
			rescheduleMutation.mutate({
				path: { dealId, programmeId, id },
				body: { nextFireAt },
			}),
		isReschedulePending: rescheduleMutation.isPending,
		restart: (id: string) => restartMutation.mutate({ path: { dealId, programmeId, id } }),
		isRestartPending: restartMutation.isPending,
		sendNow: (id: string) => sendNowMutation.mutate({ path: { dealId, programmeId, id } }),
		isSendNowPending: sendNowMutation.isPending,
	};
};
